import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SummaryItem {
  range: string
  label: string
  attendance_count: number
}

interface SummaryResponse {
  base_date: string
  items: SummaryItem[]
}

interface AttendanceMember {
  member_id: number
  login_id: string
  name: string
  total_attendance_count: number
  first_attendance_date: string
  last_attendance_date: string
}

interface AttendanceListResponse {
  total: number
  members: AttendanceMember[]
}

type SortOption = 'total_attendance_desc' | 'last_attendance_desc' | 'first_attendance_asc'

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'total_attendance_desc', label: '출석 많은 순' },
  { value: 'last_attendance_desc', label: '최근 출석 순' },
  { value: 'first_attendance_asc', label: '첫 출석 빠른 순' },
]

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminAttendanceList() {
  const navigate = useNavigate()

  // summary
  const [summary, setSummary] = useState<SummaryItem[]>([])
  const [summaryLoading, setSummaryLoading] = useState(true)

  // list
  const [members, setMembers] = useState<AttendanceMember[]>([])
  const [total, setTotal] = useState(0)
  const [listLoading, setListLoading] = useState(true)

  // filters
  const [loginIdInput, setLoginIdInput] = useState('')
  const [nameInput, setNameInput] = useState('')
  const [loginIdSearch, setLoginIdSearch] = useState('')
  const [nameSearch, setNameSearch] = useState('')
  const [sort, setSort] = useState<SortOption>('total_attendance_desc')
  const [skip, setSkip] = useState(0)
  const limit = 20

  // ── fetch summary ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetch = async () => {
      setSummaryLoading(true)
      try {
        const res = await fetchWithAuth(getApiUrl('/marubit/api/attendance/stats/summary'))
        if (!res.ok) throw new Error()
        const data: SummaryResponse = await res.json()
        setSummary(data.items)
      } catch {
        // 실패해도 목록은 보여줌
      } finally {
        setSummaryLoading(false)
      }
    }
    fetch()
  }, [])

  // ── fetch list ─────────────────────────────────────────────────────────────
  const fetchList = useCallback(async (
    loginId: string, name: string, sortVal: SortOption, skipVal: number,
  ) => {
    setListLoading(true)
    try {
      const params = new URLSearchParams()
      if (loginId) params.append('login_id', loginId)
      if (name) params.append('name', name)
      params.append('sort', sortVal)
      params.append('skip', String(skipVal))
      params.append('limit', String(limit))

      const res = await fetchWithAuth(getApiUrl(`/marubit/api/attendance/admin/list?${params}`))
      if (!res.ok) throw new Error()
      const data: AttendanceListResponse = await res.json()
      setMembers(data.members)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setListLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchList(loginIdSearch, nameSearch, sort, skip)
  }, [loginIdSearch, nameSearch, sort, skip, fetchList])

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleSearch = () => {
    setLoginIdSearch(loginIdInput)
    setNameSearch(nameInput)
    setSkip(0)
  }

  const handleReset = () => {
    setLoginIdInput('')
    setNameInput('')
    setLoginIdSearch('')
    setNameSearch('')
    setSkip(0)
  }

  const handleSortChange = (val: SortOption) => {
    setSort(val)
    setSkip(0)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const formatDate = (d: string | null) => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })
  }

  const totalPages = Math.ceil(total / limit)
  const currentPage = Math.floor(skip / limit) + 1

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">출석 관리</h1>
          <p className="text-[13px] text-gray-400">
            {new Date().toLocaleDateString('ko-KR', {
              year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
            })}
          </p>
        </div>

        {/* ── 출석 현황 카드 ── */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {summaryLoading
            ? [1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)] animate-pulse">
                  <div className="h-3 w-16 bg-gray-100 rounded mb-3" />
                  <div className="h-8 w-12 bg-gray-100 rounded" />
                </div>
              ))
            : summary.map(item => (
                <div
                  key={item.range}
                  className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)]"
                >
                  <p className="text-[12px] text-gray-400 font-medium mb-2">{item.label} 출석</p>
                  <p className="text-[32px] font-extrabold text-[#2a1f1a] leading-none">
                    {item.attendance_count.toLocaleString()}
                    <span className="text-[14px] font-medium text-gray-400 ml-1">명</span>
                  </p>
                </div>
              ))
          }
        </div>

        {/* ── 검색 + 정렬 ── */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-3 items-end justify-between">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">아이디</label>
              <input
                value={loginIdInput}
                onChange={e => setLoginIdInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="아이디 검색"
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[160px] transition-colors"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">이름</label>
              <input
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="이름 검색"
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[160px] transition-colors"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors h-[38px]"
              >
                검색
              </button>
              <button
                onClick={handleReset}
                className="bg-white text-[#887267] text-[13px] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] cursor-pointer hover:bg-[#f5f2f0] transition-colors h-[38px]"
              >
                초기화
              </button>
            </div>
          </div>

          {/* 정렬 탭 */}
          <div className="flex gap-1">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleSortChange(opt.value)}
                className={`text-[12px] font-medium px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                  sort === opt.value
                    ? 'bg-[#887267] text-white border-[#887267]'
                    : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── 목록 테이블 ── */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          {listLoading ? (
            <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">
              불러오는 중...
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[48px]">#</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">아이디</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">이름</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[100px]">총 출석수</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">첫 출석일</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">최근 출석일</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-300 text-[13px]">
                      {loginIdSearch || nameSearch ? '검색 결과가 없습니다.' : '출석 데이터가 없습니다.'}
                    </td>
                  </tr>
                ) : members.map((member, idx) => (
                  <tr
                    key={member.member_id}
                    onClick={() => navigate(`/admin/attendance/${member.login_id}`)}
                    className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-[13px] text-gray-400 whitespace-nowrap">
                      {skip + idx + 1}
                    </td>
                    <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a] whitespace-nowrap">
                      {member.login_id}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">
                      {member.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-[13px] font-bold text-[#887267]">
                        {member.total_attendance_count.toLocaleString()}
                      </span>
                      <span className="text-[11px] text-gray-400 ml-0.5">일</span>
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">
                      {formatDate(member.first_attendance_date)}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">
                      {formatDate(member.last_attendance_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 총 건수 */}
        {!listLoading && total > 0 && (
          <p className="text-[12px] text-gray-400 mt-3 text-right">
            총 <span className="font-semibold text-[#887267]">{total}</span>명
          </p>
        )}

        {/* ── 페이지네이션 ── */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1.5 mt-4">
            <button
              onClick={() => setSkip((currentPage - 2) * limit)}
              disabled={currentPage === 1}
              className="w-8 h-8 rounded-lg text-[12px] font-medium border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] transition-colors cursor-pointer"
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                  acc.push('...')
                }
                acc.push(p)
                return acc
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span key={`el-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setSkip(((p as number) - 1) * limit)}
                    className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
                      currentPage === p
                        ? 'bg-[#887267] text-white border-[#887267]'
                        : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              onClick={() => setSkip(currentPage * limit)}
              disabled={currentPage === totalPages}
              className="w-8 h-8 rounded-lg text-[12px] font-medium border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] transition-colors cursor-pointer"
            >
              ›
            </button>
          </div>
        )}

      </div>
    </div>
  )
}